// @spec VUL-STR-055
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Building2 } from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SubsidiaryVulnerabilitiesView } from '@/features/vulnerabilities/components/SubsidiaryVulnerabilitiesView';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/vulnerabilities/subsidiaries')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.subsidiaryVulnerabilities'),
  }),
  component: SubsidiaryVulnerabilitiesPage,
});

/**
 * Page for viewing vulnerabilities from subsidiary organizations.
 *
 * Features:
 * - Aggregate metrics across all subsidiaries
 * - Per-subsidiary breakdown
 * - Filter vulnerabilities by subsidiary
 * - Read-only view of subsidiary vulnerability data
 * - Export consolidated reports
 */
function SubsidiaryVulnerabilitiesPage() {
  const { t } = useTranslation('common');
  const { organization, isLoaded } = useOrganization();

  if (!isLoaded) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-7xl">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-2 h-4 w-96" />
          <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <Building2 className="h-12 w-12 text-muted-foreground" />
        <h2 className="mt-4 text-xl font-semibold">{t('common:routeErrors.noOrganization')}</h2>
        <p className="mt-2 text-muted-foreground">
          {t('common:routeErrors.noOrgSelectedDesc')}
        </p>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <header className="flex items-center gap-4">
          <Link to="/vulnerabilities">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-semibold text-foreground">Subsidiary Vulnerabilities</h1>
            <p className="mt-1 text-muted-foreground">
              View and analyze vulnerabilities across subsidiary organizations
            </p>
          </div>
        </header>

        {/* Main view */}
        <SubsidiaryVulnerabilitiesView organizationId={organization.id} />
      </div>
    </div>
  );
}
